/* eslint-disable max-len */

import { ReactNode } from 'react';
import { Badge, Divider, Popover } from 'antd';
import apiRequests from 'utils/api';
import apiRoutes from 'config/apiRoute';
import useWebSocket from 'utils/useWebSocket';
import useLiveCounter from 'utils/useLiveCounter';
import { Link } from 'react-router-dom';
import styles from './styles.module.less';

type CountType = {
  overdue?: number;
  needs_attention?: number;
  without_request?: number;
  total: number;
};

interface CountTaskBadgeProps {
  children?: ReactNode;
  collapsed?: boolean;
}

const fetchTaskCounts = async (abortController: AbortController): Promise<CountType> => {
  const response = await apiRequests.get(apiRoutes.COUNTERS, {}, {}, abortController, true);

  const { overdue, without_request: withoutRequest, per_status: perStatus } = response.data.tasks;
  const needsAttention = perStatus?.needs_attention;

  return {
    ...(overdue ? { overdue } : {}),
    ...(needsAttention ? { needs_attention: needsAttention } : {}),
    ...(withoutRequest ? { without_request: withoutRequest } : {}),
    total: (overdue ?? 0) + (needsAttention ?? 0),
  };
};

const CountTaskBadge = ({ children, collapsed }: CountTaskBadgeProps) => {
  const { value: count, refresh } = useLiveCounter<CountType>({ fetcher: fetchTaskCounts });

  const totalIssues = (count?.overdue ?? 0) + (count?.needs_attention ?? 0) + (count?.without_request ?? 0);

  const popoverContent = (
    <div style={{ minWidth: 100 }}>
      <div style={{ fontWeight: 'bold', lineHeight: '1', display: 'flex' }}>
        <span>Total issues:</span>
        <span style={{ marginLeft: 'auto' }}>{totalIssues}</span>
      </div>
      <Divider style={{ margin: '4px 0' }} />
      <div className={styles.tasksList}>
        {count?.overdue && (
          <Link to="/tasks?overdue_only=true">
            <span>Overdue:</span>
            <span>{count?.overdue}</span>
          </Link>
        )}
        {count?.needs_attention && (
          <Link to="/tasks?task_status=needs_attention">
            <span>Needs attention:</span>
            <span>{count?.needs_attention}</span>
          </Link>
        )}
        {count?.without_request && (
          <Link to="/tasks?has_requests=false">
            <span>Without requests:</span>
            <span>{count?.without_request}</span>
          </Link>
        )}
      </div>
    </div>
  );

  useWebSocket({
    channelName: 'model.changes',
    listen: {
      event: '.app.models.projects.task',
      callback: refresh,
    },
  });

  if (totalIssues === 0) return children ? <>{children}</> : null;

  if (collapsed) {
    return (
      <Badge count={totalIssues} size="small" title="" style={{ position: 'absolute', right: '-20px', bottom: '0px' }} />
    );
  }

  if (children) {
    return (
      <Badge count={totalIssues} size="small" title="" offset={[-5, 5]}>
        {children}
      </Badge>
    );
  }

  return (
    <Popover content={popoverContent} placement="right">
      <Badge count={totalIssues} title="" />
    </Popover>
  );
};

export default CountTaskBadge;
